import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Sparkles, ArrowRight, Activity, BookOpen, Settings as SettingsIcon, MapPin, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import HealthScore from "../components/HealthScore";

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [groupes, setGroupes] = useState([]);
  const [globalData, setGlobalData] = useState(null);
  const [primaryGroup, setPrimaryGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: groups } = await api.get("/groupes");
        setGroupes(groups);
        if (groups.length && user.role !== "ceo" && user.role !== "dn") {
          const { data: detail } = await api.get(`/groupes/${groups[0].id}`);
          setPrimaryGroup(detail);
        }
        if (user.role === "ceo" || user.role === "dn") {
          const { data: g } = await api.get("/dashboard/global");
          setGlobalData(g);
        }
      } catch {
        // dashboard fails silently : empty state UI handles missing data
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (loading) return <DashLoading />;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero greeting */}
      <section className="bg-gradient-to-br from-apollo-orange-light/60 via-white to-bg-soft border border-border rounded-2xl p-6 md:p-10 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-apollo-orange/10 blur-3xl" />
        <div className="relative">
          <p className="text-xs uppercase tracking-widest text-apollo-orange-dark font-bold mb-2">{user.role_label_fr} · {user.ville || ""}</p>
          <h1 className="font-heading font-black text-3xl md:text-5xl tracking-tighter text-text-primary mb-2">
            {t("dashboard.welcome")}, {user.full_name?.split(" ")[0]}.
          </h1>
          <p className="font-oracle italic text-xl text-text-primary/80 max-w-2xl">
            « {user.role === "ceo" ? t("dashboard.quote_ceo") :
                user.role === "dn" ? t("dashboard.quote_dn") :
                (user.role === "dr" || user.role === "dz") ? t("dashboard.quote_dr") :
                t("dashboard.quote_member")} »
          </p>
        </div>
      </section>

      {/* Role-specific content */}
      {(user.role === "ceo" || user.role === "dn") && globalData && (
        <GlobalDashboard data={globalData} groupes={groupes} role={user.role} />
      )}

      {(user.role === "dr" || user.role === "dz") && primaryGroup && (
        <DRDashboard data={primaryGroup} />
      )}

      {(user.role === "membre" || user.role === "consultant" || user.role === "president" || user.role === "coach") && primaryGroup && (
        <MemberDashboard data={primaryGroup} role={user.role} />
      )}

      {/* Quick actions */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quick-actions">
        <Link to="/catalogue" className="bg-white border border-border rounded-xl p-4 hover:border-apollo-orange/40 hover:shadow-md transition-all group" data-testid="quick-action-catalogue">
          <BookOpen className="w-5 h-5 text-apollo-orange mb-2" />
          <div className="font-heading font-bold text-sm">{t("dashboard.quick_library")}</div>
          <div className="text-xs text-text-secondary">{t("dashboard.quick_library_sub")}</div>
        </Link>
        {user.role === "ceo" && (
          <Link to="/editor" className="bg-white border border-border rounded-xl p-4 hover:border-apollo-orange/40 hover:shadow-md transition-all" data-testid="quick-action-editor">
            <SettingsIcon className="w-5 h-5 text-apollo-orange mb-2" />
            <div className="font-heading font-bold text-sm">{t("dashboard.quick_editor")}</div>
            <div className="text-xs text-text-secondary">{t("dashboard.quick_editor_sub")}</div>
          </Link>
        )}
        {(() => {
          // Prefer the user's OWN group as "primary" : falls back to the first
          // visible group if the user has none assigned.
          const primary = groupes.find((g) => g.id === user.groupe_id) || groupes[0];
          if (!primary) return null;
          return (
            <>
              <Link to={`/groupe/${primary.id}`} className="bg-white border border-border rounded-xl p-4 hover:border-apollo-orange/40 hover:shadow-md transition-all" data-testid="quick-action-group">
                <Users className="w-5 h-5 text-apollo-orange mb-2" />
                <div className="font-heading font-bold text-sm">{t("dashboard.quick_group")}</div>
                <div className="text-xs text-text-secondary">{primary.nom}</div>
              </Link>
              {(user.role === "dr" || user.role === "dn" || user.role === "ceo") && (
                <Link to={`/groupe/${primary.id}/diagnostic`} className="bg-apollo-orange text-white rounded-xl p-4 hover:bg-apollo-orange-dark transition-all" data-testid="quick-action-diagnostic">
                  <Sparkles className="w-5 h-5 mb-2" />
                  <div className="font-heading font-bold text-sm">{t("dashboard.quick_diagnostic")}</div>
                  <div className="text-xs text-white/80">{t("dashboard.quick_diagnostic_sub")}</div>
                </Link>
              )}
            </>
          );
        })()}
      </section>
    </div>
  );
}

function DashLoading() {
  return (
    <div className="space-y-6 animate-pulse" data-testid="dashboard-loading">
      <div className="h-40 bg-bg-soft rounded-2xl" />
      <div className="grid grid-cols-3 gap-4">
        <div className="h-32 bg-bg-soft rounded-xl" />
        <div className="h-32 bg-bg-soft rounded-xl" />
        <div className="h-32 bg-bg-soft rounded-xl" />
      </div>
    </div>
  );
}

function GlobalDashboard({ data, groupes, role }) {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || "fr").startsWith("en") ? "en-GB" : "fr-FR";
  return (
    <>
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="global-kpis">
        <StatCard label={t("dashboard.diag_this_month")} value={data.diag_this_month} testId="stat-diag-month" />
        <StatCard label={t("dashboard.diag_total")} value={data.diag_total} testId="stat-diag-total" />
        <StatCard label={t("dashboard.avg_score")} value={data.avg_score || "n/a"} suffix="/100" testId="stat-avg-score" />
        {data.last_diag_stale ? (
          <div className="bg-white border border-border rounded-xl p-4" data-testid="stat-last-analysis">
            <div className="text-xs text-text-secondary uppercase tracking-wider">{t("dashboard.analysis_of_label")}</div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300" data-testid="dashboard-stale-badge">
                {t("group.stale_badge")}
              </span>
              {data.stale_group_id && (
                <Link to={`/groupe/${data.stale_group_id}/diagnostic`} className="text-xs font-bold text-apollo-orange hover:underline" data-testid="dashboard-relaunch-link">
                  {t("dashboard.relaunch")}
                </Link>
              )}
            </div>
          </div>
        ) : (
          <StatCard
            label={t("dashboard.analysis_of_label")}
            value={data.last_diag_at ? new Date(data.last_diag_at).toLocaleDateString(dateLocale) : "n/a"}
            testId="stat-last-analysis"
          />
        )}
      </section>

      <section className="bg-white border border-border rounded-xl p-6" data-testid="groups-health-heatmap">
        <h2 className="font-heading font-bold text-xl tracking-tight mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-apollo-orange" /> {t("dashboard.groups_health")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {data.groups_health.map((g) => (
            <Link key={g.id} to={`/groupe/${g.id}`} data-testid={`group-tile-${g.id}`}
              className="border border-border rounded-lg p-4 hover:border-apollo-orange/40 hover:shadow-md transition-all">
              <div className="text-xs text-text-secondary mb-1">{g.ville} · {g.region}</div>
              <div className="font-heading font-bold text-base mb-2">{g.nom}</div>
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-xs text-text-secondary">{t("dashboard.phase")}</div>
                  <div className="text-sm font-medium">{t(`phase.${g.phase}`, g.phase)}</div>
                </div>
                {g.sante !== null && g.sante !== undefined ? (
                  <div className="text-right">
                    <div className="text-xs text-text-secondary">{t("dashboard.health")}</div>
                    <div className={`font-heading font-black text-2xl ${
                      g.sante >= 70 ? "text-success" : g.sante >= 40 ? "text-warning" : "text-danger"
                    }`}>{g.sante}</div>
                  </div>
                ) : (
                  <div className="text-xs text-text-secondary italic">{t("dashboard.no_diagnostic_yet")}</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {data.top_ateliers?.length > 0 && (
        <section className="bg-white border border-border rounded-xl p-6" data-testid="top-ateliers-section">
          <h2 className="font-heading font-bold text-xl tracking-tight mb-4">{t("dashboard.top_ateliers")}</h2>
          <div className="space-y-2">
            {data.top_ateliers.map((tatk, i) => (
              <Link key={tatk.atelier_id} to={`/atelier/${tatk.atelier_id}`} data-testid={`top-atelier-${tatk.atelier_id}`}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-bg-soft transition-colors">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-mono text-text-secondary w-6">#{i + 1}</span>
                  <span className="font-mono text-xs px-2 py-0.5 rounded bg-bg-soft border border-border">{tatk.atelier_id}</span>
                  <span className="text-sm font-medium">{tatk.title}</span>
                </div>
                <span className="text-sm font-bold text-apollo-orange-dark">{tatk.count} ×</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function DRDashboard({ data }) {
  const { t, i18n } = useTranslation();
  const isEn = (i18n.language || "fr").startsWith("en");
  const { group, last_diagnostic, active_prescriptions, members_count, latest_kpi } = data;
  void latest_kpi;
  const score = last_diagnostic?.sante_globale;
  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="dr-dashboard">
      <div className="bg-white border border-border rounded-xl p-6 md:col-span-2">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs text-text-secondary uppercase tracking-wider">{group.region}</div>
            <h2 className="font-heading font-black text-2xl tracking-tight">{group.nom}</h2>
            <div className="text-sm text-text-secondary mt-1">{members_count} {isEn ? "members" : "membres"} · {t("dashboard.phase")} {t(`phase.${group.phase}`, group.phase)}</div>
          </div>
          {score !== undefined && (
            <div className={`px-3 py-1 rounded-full text-sm font-bold ${
              score >= 70 ? "bg-success/10 text-success" : score >= 40 ? "bg-warning/10 text-warning" : "bg-danger/10 text-danger"
            }`}>{score}/100</div>
          )}
        </div>

        {last_diagnostic ? (
          <p className="font-oracle italic text-lg text-text-primary leading-relaxed line-clamp-3">
            « {last_diagnostic.etat_synthese} »
          </p>
        ) : (
          <div className="bg-apollo-orange-light/50 border border-apollo-orange/20 rounded-lg p-4">
            <p className="text-sm text-apollo-orange-dark font-medium">{isEn ? "No diagnostic run yet for this group. The oracle awaits your signal." : "Aucun diagnostic encore lancé pour ce groupe. L'oracle attend votre signal."}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-5">
          <Link to={`/groupe/${group.id}`} data-testid="btn-voir-groupe">
            <Button variant="outline" className="border-border">{isEn ? "View group" : "Voir le groupe"}</Button>
          </Link>
          <Link to={`/groupe/${group.id}/diagnostic`} data-testid="btn-lancer-diagnostic">
            <Button className="bg-apollo-orange hover:bg-apollo-orange-dark text-white">
              <Sparkles className="w-4 h-4 mr-2" /> {t("group.run_diagnostic")}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-6 flex flex-col items-center justify-center">
        {score !== undefined ? (
          <HealthScore value={score} testId="dr-health-score" />
        ) : (
          <div className="text-center">
            <Activity className="w-12 h-12 text-text-secondary mx-auto mb-3" />
            <div className="text-sm text-text-secondary">{isEn ? "No health score yet" : "Pas encore de score de santé"}</div>
          </div>
        )}
      </div>

      {active_prescriptions?.length > 0 && (
        <div className="md:col-span-3 bg-white border border-border rounded-xl p-6" data-testid="active-prescriptions">
          <h3 className="font-heading font-bold text-lg mb-3">{isEn ? "Workshops to run" : "Ateliers à faire"}</h3>
          <div className="space-y-2">
            {active_prescriptions.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-bg-soft rounded-lg" data-testid={`prescription-${p.id}`}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs px-2 py-0.5 rounded bg-white border border-border">{p.atelier_id}</span>
                  <span className="text-sm">{p.raisonnement.slice(0, 80)}…</span>
                </div>
                <Link to={`/atelier/${p.atelier_id}`}>
                  <Button variant="ghost" size="sm">{isEn ? "View" : "Voir"} <ArrowRight className="w-3 h-3 ml-1" /></Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function MemberDashboard({ data, role }) {
  const { i18n } = useTranslation();
  const isEn = (i18n.language || "fr").startsWith("en");
  const { group, members_count, last_diagnostic } = data;
  const score = last_diagnostic?.sante_globale;

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="member-dashboard">
      <div className="bg-white border border-border rounded-xl p-6">
        <div className="text-xs text-text-secondary uppercase tracking-wider mb-1">{isEn ? "My group" : "Mon groupe"}</div>
        <h2 className="font-heading font-black text-2xl tracking-tight">{group.nom}</h2>
        <div className="text-sm text-text-secondary mt-1">{members_count} {isEn ? "members" : "membres"} · {group.ville}</div>
        <div className="mt-4">
          <Link to={`/groupe/${group.id}`}>
            <Button variant="outline" className="border-border" data-testid="btn-explorer-groupe">{isEn ? "Explore my group" : "Explorer mon groupe"}</Button>
          </Link>
        </div>
      </div>
      {role === "consultant" && score !== undefined && (
        <div className="bg-white border border-border rounded-xl p-6 flex items-center justify-center">
          <HealthScore value={score} label={isEn ? "Group health" : "Santé du groupe"} testId="member-health-score" />
        </div>
      )}
      {role === "membre" && (
        <div className="bg-white border border-border rounded-xl p-6">
          <h3 className="font-heading font-bold text-lg mb-2">{isEn ? "My BPM profile" : "Mon profil BPM"}</h3>
          <p className="text-sm text-text-secondary">{isEn ? "Connect to APOLLO BPM to discover your profile and your group's." : "Connectez-vous au BPM APOLLO pour découvrir votre profil et celui de votre groupe."}</p>
        </div>
      )}
    </section>
  );
}

function StatCard({ label, value, suffix = "", testId }) {
  return (
    <div className="bg-white border border-border rounded-xl p-4" data-testid={testId}>
      <div className="text-xs text-text-secondary uppercase tracking-wider">{label}</div>
      <div className="font-heading font-black text-3xl text-text-primary tracking-tighter mt-1">
        {value}<span className="text-base text-text-secondary font-medium ml-1">{suffix}</span>
      </div>
    </div>
  );
}
